import { useState, type FormEvent } from "react";
import { isAgentQuestionsAnswered, type AgentAnswer, type AgentQuestion } from "./agent-questions";

// The Claude-style "answer these" card shown in the task drawer for an open
// question set from the Coding Assistant. One question per block: choice chips
// (radio, or checkbox when multiSelect) plus an always-available "Other" free
// text box; option-less questions are just a free-text box. Submitting hands
// the structured answers to the parent, which posts them as a normal comment.

type Props = {
  busy: boolean;
  intro: string;
  onSubmit: (answers: AgentAnswer[]) => void;
  questions: AgentQuestion[];
};

export const AgentQuestionsCard = ({ busy, intro, onSubmit, questions }: Props) => {
  const [answers, setAnswers] = useState<AgentAnswer[]>(() => questions.map((question) => ({ questionId: question.id, selected: [] })));
  const update = (questionId: string, change: (answer: AgentAnswer) => AgentAnswer) =>
    setAnswers((current) => current.map((answer) => (answer.questionId === questionId ? change(answer) : answer)));
  const toggle = (question: AgentQuestion, label: string) => update(question.id, (answer) => {
    const has = answer.selected.includes(label);
    if (question.multiSelect) return { ...answer, selected: has ? answer.selected.filter((value) => value !== label) : [...answer.selected, label] };
    return { ...answer, selected: has ? [] : [label] };
  });
  const complete = isAgentQuestionsAnswered(questions, answers);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (complete && !busy) onSubmit(answers);
  };
  return (
    <form aria-label="Questions from Coding Assistant" className="agent-questions" onSubmit={submit}>
      <p className="agent-questions-intro">{intro}</p>
      {questions.map((question, index) => {
        const answer = answers[index]!;
        return (
          <fieldset className="agent-question" key={question.id}>
            <legend>
              {question.header && <span className="agent-question-chip">{question.header}</span>}
              {question.question}
            </legend>
            {question.options && (
              <div className="agent-question-options">
                {question.options.map((option) => {
                  const selected = answer.selected.includes(option.label);
                  return (
                    <label className={selected ? "agent-option selected" : "agent-option"} key={option.label}>
                      <input
                        checked={selected}
                        name={`agent-q-${question.id}`}
                        onChange={() => toggle(question, option.label)}
                        type={question.multiSelect ? "checkbox" : "radio"}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        {option.description && <small>{option.description}</small>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <input
              aria-label={question.options ? `Other answer for: ${question.question}` : `Answer for: ${question.question}`}
              className="agent-question-other"
              maxLength={1000}
              onChange={(event) => update(question.id, (current) => ({ ...current, other: event.target.value }))}
              placeholder={question.options ? "Other…" : "Type your answer"}
              type="text"
              value={answer.other ?? ""}
            />
          </fieldset>
        );
      })}
      <div className="agent-questions-actions">
        <button className="button primary" disabled={!complete || busy} type="submit">{busy ? "Sending…" : "Send answers"}</button>
      </div>
    </form>
  );
};
